import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ContentsClient, ContentListDto, SetupRequirement } from '../api/api-client';
import { CryptoHelper } from '../utils/cryptoHelper';

interface DecryptedContent extends ContentListDto {
  decryptedTitle?: string;
  contentKey?: CryptoKey;
}

/**
 * Hook to fetch and decrypt content list for sidebar and other components
 */
export const useContents = () => {
  const { isAuthenticated, masterKey, setupNeeded } = useAuth();

  return useQuery({
    queryKey: ['contents'],
    queryFn: async (): Promise<DecryptedContent[]> => {
      const client = new ContentsClient();
      const contentList = await client.getAccessibleContents();

      // If no master key, return content without decrypted titles
      if (!masterKey) {
        return contentList.map(content => ({
          ...content,
          decryptedTitle: 'Encrypted Thread'
        }));
      }

      // Decrypt titles
      const decryptedContents = await Promise.all(
        contentList.map(async (content) => {
          try {
            if (content.id !== undefined && content.encryptedContentKey && content.title) {
              const contentKey = await CryptoHelper.decryptContentKey(content.encryptedContentKey, masterKey);
              const decryptedTitle = await CryptoHelper.decryptContent(content.title, contentKey);
              return {
                ...content,
                decryptedTitle,
                contentKey
              };
            }
          } catch (error) {
            console.warn(`Failed to decrypt title for content ${content.id}:`, error);
          }
          
          return {
            ...content,
            decryptedTitle: 'Encrypted Thread'
          };
        })
      );

      return decryptedContents;
    },
    enabled: isAuthenticated && setupNeeded === SetupRequirement.None && !!masterKey,
  });
};

export type { DecryptedContent };